import { Button, Skeleton } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import styles from './DashboardCard.module.scss';
import { Props } from './DashboardCard.types';

/**
 * Common skeleton of the three cards of the Finances band: label -> key figure ->
 * subline -> actions pinned to the bottom. The three cards share it so they end up
 * exactly the same height, instead of the two short ones leaving white holes next to
 * the tall budget card.
 *
 * Loading and error are handled here, per card: a failing card shows its own retry
 * button and leaves the two others fully usable.
 */
const DashboardCard = ({
  label,
  figure,
  subline,
  extra,
  actions,
  isLoading,
  isError,
  onRetry,
}: Props) => {
  const { t } = useTranslation();

  const renderContent = () => {
    if (isLoading) {
      // Same vertical footprint as the loaded card, so nothing jumps once data lands.
      return (
        <>
          <Skeleton height={34} width="70%" radius="sm" mt={6} />
          <Skeleton height={16} width="55%" radius="sm" mt={10} />
        </>
      );
    }

    if (isError) {
      return (
        <p className={styles.errorText}>
          {t('expenses.dashboard.loadError', "Impossible d'afficher cette information.")}
        </p>
      );
    }

    return (
      <>
        <div className={styles.figure}>{figure}</div>
        {subline && <div className={styles.subline}>{subline}</div>}
        {extra}
      </>
    );
  };

  const renderActions = () => {
    if (isLoading) return <Skeleton height={40} width={150} radius="sm" />;

    if (isError) {
      return onRetry ? (
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t('common.retry', 'Réessayer')}
        </Button>
      ) : null;
    }

    return actions;
  };

  return (
    <section className={styles.container}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        {renderContent()}
      </div>
      <div className={styles.actions}>{renderActions()}</div>
    </section>
  );
};

export default DashboardCard;
